import {
  CreditTypeEnum,
  IAccountRespSchema,
  TransactableSourceType,
} from '@monetize/types/app';
import { FilterType } from '@monetize/types/common';
import React, { useCallback, useMemo, useRef } from 'react';
import { Link as ReactDomLink, useNavigate } from 'react-router-dom';
import {
  MBox,
  MButton,
  MFlex,
  MHStack,
  MPageHeader,
  MPageSearchInput,
} from '../../../components/Monetize';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { ROUTES } from '../../../constants';
import {
  getAccountCreditNoteViewRoute,
  getAccountCreditsViewRoute,
} from '../../../constants/routes';
import {
  ICreditWithBillGroup,
  useCredits,
} from '../../../hooks/AccountDetail/useCredits';
import { useACL } from '../../../services/acl/acl';
import { ACLCheck } from '../../../services/acl/ACLCheck';
import { getIsTrulyEmptyList } from '../../../utils';
import { CreditActions } from './CreditActions';
import {
  CREDITS_LIST_CATALOG,
  CreditsListColumnContext,
} from './creditsList.columns';

export const CreditsList = ({
  account,
  billGroupId,
}: {
  account: IAccountRespSchema;
  billGroupId?: string;
}) => {
  const navigate = useNavigate();
  const { id: accountId = '' } = account;
  const {
    filters,
    setFilters,
    searchKey,
    searchTerm,
    setSearchTerm,
    onResetFilter,
    listData,
    pager,
    setPager,
    loading,
    refetch,
  } = useCredits(accountId, billGroupId);

  const { canDo } = useACL();
  const canUpdateCredit = canDo([['billing', 'update']]);

  const tableRef = useRef<DataTableRef>(null);

  const actionBodyTemplate = useCallback(
    (credit: ICreditWithBillGroup) => (
      <CreditActions
        sourceType={
          credit.type === CreditTypeEnum.CREDIT_NOTE
            ? TransactableSourceType.creditNote
            : TransactableSourceType.credit
        }
        accountId={credit.accountId}
        applicationsSourceId={credit.id}
        amount={credit.amount}
        appliedAmount={credit.amountApplied}
        currency={credit.currency}
        billGroupId={credit.billGroupId}
        creditStatus={credit.status}
        creditNoteId={credit.creditNoteId}
      />
    ),
    [],
  );

  const columnContext = useMemo<CreditsListColumnContext>(
    () => ({ accountId, actionBodyTemplate }),
    [accountId, actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    CREDITS_LIST_CATALOG,
    columnContext,
  );

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: listData?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const searchComponentReset = React.useRef<any>(null);

  const getCreditCreateRoute = (
    accountIdProp: string,
    billGroupIdProp?: string,
  ) => {
    return billGroupIdProp
      ? ROUTES.getBillGroupCreditsCreateRoute(billGroupIdProp)
      : ROUTES.getAccountCreditCreate(accountIdProp);
  };

  const handleNewTableFiltersChange = (newFilters: FilterType[]) => {
    setFilters(newFilters);
    setPager({ ...pager, page: 0, first: 0 });
  };

  const handleRowClick = (row: ICreditWithBillGroup) => {
    const { id, creditNoteId, type } = row;
    if (
      [CreditTypeEnum.SERVICE, CreditTypeEnum.PREPAID].includes(type) &&
      canUpdateCredit
    ) {
      return navigate(getAccountCreditsViewRoute(accountId, id));
    }
    if (type === CreditTypeEnum.CREDIT_NOTE && canUpdateCredit) {
      return navigate(getAccountCreditNoteViewRoute(accountId, creditNoteId!));
    }
  };

  return (
    <MBox data-testid="account-credits-list">
      <MPageHeader title="Credits" hideContent={isTrulyEmptyList}>
        <MFlex>
          <MHStack gap="2">
            <MPageSearchInput
              placeholderKey="Name"
              value={searchTerm || ''}
              onChange={(e: any) => setSearchTerm && setSearchTerm(e)}
              count={listData?.totalElements}
              resetSearch={searchComponentReset}
              mode="alwaysExpanded"
            />
            <RefreshTableButton refetch={refetch} />
            <ExportEntityButton
              entity="credits"
              filters={filters}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
              endpointParams={[accountId]}
              getFilename={() => `credits-${accountId}.csv`}
            />
            {(billGroupId || accountId) && (
              <ACLCheck acls={[['billing', 'create']]}>
                <MButton variant="secondary" asChild>
                  <ReactDomLink
                    to={getCreditCreateRoute(accountId, billGroupId)}
                  >
                    New Credit
                  </ReactDomLink>
                </MButton>
              </ACLCheck>
            )}
          </MHStack>
        </MFlex>
      </MPageHeader>
      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={listData?.content || []}
        isLoading={loading}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={listData?.totalElements || 0}
        totalPages={listData?.totalPages}
        pager={pager}
        setPager={setPager}
        onRowClick={handleRowClick}
        onFiltersChange={handleNewTableFiltersChange}
        initialFilters={filters}
        resetFilter={() => {
          onResetFilter();
        }}
        emptyProps={{
          mainMessage: 'Looks like there are no credits here.',
          btnLabel: canDo([['billing', 'create']]) ? 'New Credit' : '',
          to:
            (billGroupId || accountId) &&
            getCreditCreateRoute(accountId, billGroupId),
        }}
      />
    </MBox>
  );
};
